import 'scss/content_download_survey.scss';

import React, {Component} from 'react';
import t from 'tcomb-form';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import {FormLabel, FormMultiRadio} from 'js/form_components';
import {ENUMS} from 'js/common';
import {track} from 'js/common/analytics';

const {Form} = t.form;

const {RepurposeType: RepurposeTypeEnum, AdType: AdTypeEnum} = ENUMS;

const RepurposeType = t.enums(sn.maps.RepurposeType);
const AdType = t.enums(sn.maps.AdType);

const Survey = t.struct({
    repurpose: t.maybe(t.list(RepurposeType)),
    ad: t.maybe(t.list(AdType)),
    other: t.maybe(t.Str)
});

function surveyTemplate(locals) {
    return (
        <div>
            {locals.inputs.repurpose}
            {locals.inputs.ad}
            {locals.inputs.other}
        </div>
    );
}

const surveyOptions = {
    template: surveyTemplate,
    fields: {
        repurpose: {
            label: <FormLabel label="Repurpose on brand's owned channel:" />,
            factory: FormMultiRadio,
            noMaxSelectable: true,
            noSort: true,
            optionsMap: RepurposeType.meta.map,
            iconMap: {
                [RepurposeTypeEnum.FACEBOOK]: [
                    require('images/content_download_survey/facebook_gray.png'),
                    require('images/content_download_survey/facebook.png')
                ],
                [RepurposeTypeEnum.TWITTER]: [
                    require('images/content_download_survey/twitter_gray.png'),
                    require('images/content_download_survey/twitter.png')
                ],
                [RepurposeTypeEnum.INSTAGRAM]: [
                    require('images/content_download_survey/instagram_gray.png'),
                    require('images/content_download_survey/instagram.png')
                ],
                [RepurposeTypeEnum.PINTEREST]: [
                    require('images/content_download_survey/pinterest_gray.png'),
                    require('images/content_download_survey/pinterest.png')
                ],
                [RepurposeTypeEnum.YOUTUBE]: [
                    require('images/content_download_survey/youtube_gray.png'),
                    require('images/content_download_survey/youtube.png')
                ],
                [RepurposeTypeEnum.EMAIL]: [
                    require('images/content_download_survey/email_gray.png'),
                    require('images/content_download_survey/email.png')
                ],
                [RepurposeTypeEnum.WEBSITE]: [
                    require('images/content_download_survey/web_gray.png'),
                    require('images/content_download_survey/web.png')
                ]
            }
        },
        ad: {
            label: <FormLabel label="To run paid ad campaign:" />,
            factory: FormMultiRadio,
            noMaxSelectable: true,
            noSort: true,
            optionsMap: AdType.meta.map,
            iconMap: {
                [AdTypeEnum.FACEBOOK]: [
                    require('images/content_download_survey/facebook_gray.png'),
                    require('images/content_download_survey/facebook.png')
                ],
                [AdTypeEnum.TWITTER]: [
                    require('images/content_download_survey/twitter_gray.png'),
                    require('images/content_download_survey/twitter.png')
                ],
                [AdTypeEnum.INSTAGRAM]: [
                    require('images/content_download_survey/instagram_gray.png'),
                    require('images/content_download_survey/instagram.png')
                ],
                [AdTypeEnum.PINTEREST]: [
                    require('images/content_download_survey/pinterest_gray.png'),
                    require('images/content_download_survey/pinterest.png')
                ],
                [AdTypeEnum.YOUTUBE]: [
                    require('images/content_download_survey/youtube_gray.png'),
                    require('images/content_download_survey/youtube.png')
                ],
                [AdTypeEnum.BANNER]: [
                    require('images/content_download_survey/banner_gray.png'),
                    require('images/content_download_survey/banner.png')
                ]
            }
        },
        other: {
            label: <FormLabel>Other: &nbsp;</FormLabel>,
            type: 'textarea'
        }
    }
};

export default class ContentDownloadSurvey extends Component {
    static propTypes = {
        post: PropTypes.object.isRequired,
        handleSubmitContentDownloadSurvey: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);
        this.state = {
            showError: false
        };
    }

    handleFormSubmit = e => {
        e.preventDefault();
        const {
            brandName,
            campaignName,
            email,
            handleSubmitContentDownloadSurvey,
            post: {post_id, post_user_score, campaign_id, content_distribution_metadata}
        } = this.props;

        const username = R.path(
            ['snapshots', 'platform_account', 'platform_handle'],
            content_distribution_metadata || {}
        );
        const contentUrl = R.path(
            ['distribution_platform_post_url'],
            content_distribution_metadata
        );

        const form = this.refs.form;
        const value = form.getValue();
        const {ad, repurpose, other} = value;

        if (ad.length || repurpose.length || other) {
            this.setState({
                showError: false
            });

            // @TODO move this KISS call into the POST
            track(email, 'brand submit download survey', {
                'DLsurvey_brand-repurpose': repurpose.join(','),
                'DLsurvey_brand-paid-ad': ad.join(','),
                'DLsurvey_brand-other-use': other,
                'Post ID': post_id,
                'Creator Username': username,
                'Campaign Funnel Id': campaign_id,
                'Campaign Funnel Name': campaignName,
                'Brand Name': brandName,
                'Content Url': contentUrl,
                'Score Given': post_user_score || 'Not Scored'
            });

            /*
             * Downloads content only when survey is filled out.
             * Wrapping download button below in an anchor allows them to
             * download the content even if there is an error.
             */
            const link = document.createElement('a');
            link.href = this.props.downloadContentUrl;
            link.download = this.props.fileName;
            document.body.appendChild(link);
            link.click();

            handleSubmitContentDownloadSurvey(post_id, value);
        } else {
            this.setState({
                showError: true
            });
        }
    };

    renderHeader() {
        const {showError} = this.state;
        const headerText =
            'To improve our service, please indicate how you plan on using this content';
        const errorText = 'Please fill out the form before submitting.';

        return showError ? (
            <div className="survey-header survey-error">{errorText}</div>
        ) : (
            <div className="survey-header">{headerText}</div>
        );
    }

    renderForm() {
        const initialValue = {
            repurpose: [],
            ad: [],
            other: ''
        };

        const survey = (
            <Form ref="form" options={surveyOptions} type={Survey} value={initialValue} />
        );

        const submit = (
            <button className="submit-button btn btn-primary" onClick={this.handleFormSubmit}>
                Submit
            </button>
        );

        return (
            <form className="col-xs-6 survey-form">
                {survey}
                <div className="submit-survey">{submit}</div>
            </form>
        );
    }

    render() {
        const header = this.renderHeader();
        const form = this.renderForm();

        return (
            <div className="content-download-survey">
                {header}
                {form}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./campaign_analytics/v2/campaign_analytics_page/posts/content_download_survey/index.js